import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { RolesValues, SortingValue } from '@constants';
import {
  getAdministrationRequestApproveNonBillableState,
  profileSelectors,
  Tabs,
  useProfileAPI,
} from '@innowise-group/core';
import { Menu, MenuItem } from '@innowise-group/mui-kit';

import * as Styled from './desktop-view.styles';

interface DesktopViewProps {
  currentTab: Tabs;
  sort: string;
  tabs: { id: Tabs; name: string }[];
  sortItems: { text: string; value: string }[];
  isRecruiterMode?: boolean;
  myTabRoles: RolesValues[];
  openSortMenu: (event: React.MouseEvent<HTMLElement>) => void;
  closeSortMenu: () => void;
  anchorEl: HTMLElement | null;
  selected: number[];
  handleChangeTab: (tabId: Tabs) => () => void;
  setSortParamHandle: (sortItem: string) => (event: React.MouseEvent) => void;
  ActionsComponent: React.FC;
}

const DesktopView: React.FC<DesktopViewProps> = ({
  currentTab,
  tabs,
  myTabRoles,
  handleChangeTab,
  isRecruiterMode,
  sort,
  selected,
  closeSortMenu,
  anchorEl,
  openSortMenu,
  sortItems,
  setSortParamHandle,
  ActionsComponent,
}) => {
  const { t } = useTranslation();

  const nonBillableEmployeeApprovers = useSelector(getAdministrationRequestApproveNonBillableState);
  const currentEmployeeId = useSelector(profileSelectors.getProfileIdState);
  const { profile } = useProfileAPI();

  const isApprover = useMemo(
    () => (nonBillableEmployeeApprovers || []).some(({ employeeId }) => employeeId === currentEmployeeId),
    [nonBillableEmployeeApprovers, currentEmployeeId],
  );

  const isAdmin = profile?.role?.name === RolesValues.Admin;
  const canSeeMyTab = useMemo(() => myTabRoles.includes(profile?.role?.name), [myTabRoles, profile?.role?.name]);

  return (
    <Styled.CustomTabs value={currentTab}>
      {tabs.map((item) => {
        if (item.id === Tabs.My && canSeeMyTab) {
          return (
            <Styled.CustomTab
              key={item.id}
              label={t(item.name) || ''}
              value={item.id}
              tabsvalue={currentTab}
              {...(currentTab !== item.id && { onClick: handleChangeTab(item.id) })}
            />
          );
        }
        if (item.id === Tabs.All) {
          return (
            <Styled.CustomTab
              key={item.id}
              label={t(item.name) || ''}
              value={item.id}
              tabsvalue={currentTab}
              {...(currentTab !== item.id && { onClick: handleChangeTab(item.id) })}
            />
          );
        }
        if (item.id === Tabs.UnderApproval && isApprover) {
          return (
            <Styled.CustomTab
              key={item.id}
              label={t(item.name) || ''}
              value={item.id}
              tabsvalue={currentTab}
              {...(currentTab !== item.id && { onClick: handleChangeTab(item.id) })}
            />
          );
        }
        if (item.id === Tabs.Archive && (isApprover || isAdmin)) {
          return (
            <Styled.CustomTab
              key={item.id}
              label={t(item.name) || ''}
              value={item.id}
              tabsvalue={currentTab}
              {...(currentTab !== item.id && { onClick: handleChangeTab(item.id) })}
            />
          );
        }
        if (item.id !== Tabs.UnderApproval && !isRecruiterMode && isAdmin) {
          return (
            <Styled.CustomTab
              key={item.id}
              label={t(item.name) || ''}
              value={item.id}
              tabsvalue={currentTab}
              {...(currentTab !== item.id && { onClick: handleChangeTab(item.id) })}
            />
          );
        }
      })}
      <Styled.TableFilters>
        {!isRecruiterMode && <ActionsComponent />}
        <Styled.GrayButton startIcon={<Styled.FilterIcon type="u_filter" />} onClick={openSortMenu} variant="text">
          <span>{t('pages.candidates.allCandidatesTable.sort')}</span>
        </Styled.GrayButton>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeSortMenu}>
          {sortItems.map((item, index) => (
            <MenuItem
              key={index}
              selected={sort === item.value}
              onClick={!selected.length && item.value === SortingValue.Checked ? null : setSortParamHandle(item.value)}
              disabled={!selected.length && item.value === SortingValue.Checked}
            >
              {t(item.text)}
            </MenuItem>
          ))}
        </Menu>
      </Styled.TableFilters>
    </Styled.CustomTabs>
  );
};

export default DesktopView;
